// Chess Game Logic
// Implements FIDE rules including special moves, check detection, and win conditions

// Piece notation: 'wP' = white pawn, 'bK' = black king, etc.
// Board is 8x8 array where [0][0] is a1, [7][7] is h8

const PIECES = {
  WHITE_KING: 'wK',
  WHITE_QUEEN: 'wQ',
  WHITE_ROOK: 'wR',
  WHITE_BISHOP: 'wB',
  WHITE_KNIGHT: 'wN',
  WHITE_PAWN: 'wP',
  BLACK_KING: 'bK',
  BLACK_QUEEN: 'bQ',
  BLACK_ROOK: 'bR',
  BLACK_BISHOP: 'bB',
  BLACK_KNIGHT: 'bN',
  BLACK_PAWN: 'bP',
};

// Initialize the board with standard chess starting position
const createStandardBoard = () => {
  // Use empty strings instead of null to avoid Firebase sparse array issues
  const board = Array(8).fill(null).map(() => Array(8).fill(''));

  // Black pieces (row 7 and 6)
  board[7][0] = PIECES.BLACK_ROOK;
  board[7][1] = PIECES.BLACK_KNIGHT;
  board[7][2] = PIECES.BLACK_BISHOP;
  board[7][3] = PIECES.BLACK_QUEEN;
  board[7][4] = PIECES.BLACK_KING;
  board[7][5] = PIECES.BLACK_BISHOP;
  board[7][6] = PIECES.BLACK_KNIGHT;
  board[7][7] = PIECES.BLACK_ROOK;
  for (let i = 0; i < 8; i++) {
    board[6][i] = PIECES.BLACK_PAWN;
  }

  // White pieces (row 0 and 1)
  board[0][0] = PIECES.WHITE_ROOK;
  board[0][1] = PIECES.WHITE_KNIGHT;
  board[0][2] = PIECES.WHITE_BISHOP;
  board[0][3] = PIECES.WHITE_QUEEN;
  board[0][4] = PIECES.WHITE_KING;
  board[0][5] = PIECES.WHITE_BISHOP;
  board[0][6] = PIECES.WHITE_KNIGHT;
  board[0][7] = PIECES.WHITE_ROOK;
  for (let i = 0; i < 8; i++) {
    board[1][i] = PIECES.WHITE_PAWN;
  }

  return board;
};

// Get piece color
const getPieceColor = (piece) => {
  if (!piece) return null;
  return piece[0] === 'w' ? 'white' : 'black';
};

// Get piece type (without color)
const getPieceType = (piece) => {
  if (!piece) return null;
  return piece[1];
};

// Check if position is valid on the board
const isValidPosition = (row, col) => {
  return row >= 0 && row < 8 && col >= 0 && col < 8;
};

// Create initial game state
export const createInitialGameState = (hostId, guestId) => {
  // Randomly assign colors
  const hostIsWhite = Math.random() < 0.5;

  return {
    board: createStandardBoard(),
    currentTurn: 'white', // White always moves first
    hostColor: hostIsWhite ? 'white' : 'black',
    guestColor: hostIsWhite ? 'black' : 'white',
    hostId,
    guestId,
    moveHistory: [],
    capturedPieces: { white: [], black: [] },
    castlingRights: {
      white: { kingside: true, queenside: true },
      black: { kingside: true, queenside: true },
    },
    enPassantTarget: null, // {row, col} if available
    halfmoveClock: 0, // For fifty-move rule
    fullmoveNumber: 1,
    inCheck: false,
    isGameOver: false,
    winner: null,
    isDraw: false,
    drawReason: null,
    selectedSquare: null,
    drawOffer: null, // {offeredBy: 'white'|'black'} when a draw is offered
  };
};

// Get all possible moves for a piece (without checking for check)
const getPossibleMoves = (board, fromRow, fromCol) => {
  console.log('getPossibleMoves called for:', { fromRow, fromCol, boardLength: board?.length });
  console.log('Board structure:', board.map((row, idx) => `Row ${idx}: ${row ? 'exists' : 'UNDEFINED'}`));
  const piece = board[fromRow][fromCol];
  if (!piece) {
    console.warn('No piece at position:', { fromRow, fromCol });
    return [];
  }

  const color = getPieceColor(piece);
  const type = getPieceType(piece);
  console.log('Piece info:', { piece, color, type });
  const moves = [];

  const addMove = (toRow, toCol) => {
    if (!isValidPosition(toRow, toCol)) return false;
    if (!board[toRow] || !Array.isArray(board[toRow])) {
      console.warn('Board row undefined at:', toRow, 'Board:', board);
      return false;
    }
    const targetPiece = board[toRow][toCol];
    // Empty squares are represented as empty strings
    if (targetPiece && targetPiece !== '' && getPieceColor(targetPiece) === color) return false;
    moves.push({ row: toRow, col: toCol });
    return !targetPiece || targetPiece === ''; // Continue if empty square
  };

  switch (type) {
    case 'P': // Pawn
      const direction = color === 'white' ? 1 : -1;
      const startRow = color === 'white' ? 1 : 6;

      console.log('Pawn movement check:', { direction, startRow, fromRow, fromCol });

      // Forward move
      const forwardRow = fromRow + direction;
      console.log('Forward move check:', {
        forwardRow,
        isValid: isValidPosition(forwardRow, fromCol),
        boardRowExists: !!board[forwardRow],
        boardRowType: typeof board[forwardRow],
        isEmpty: board[forwardRow] ? !board[forwardRow][fromCol] : 'N/A'
      });

      const isEmptySquare = (sq) => !sq || sq === '';

      if (isValidPosition(forwardRow, fromCol) && board[forwardRow] && isEmptySquare(board[forwardRow][fromCol])) {
        console.log('Adding forward move:', { row: forwardRow, col: fromCol });
        moves.push({ row: forwardRow, col: fromCol });

        // Double move from starting position
        const doubleForwardRow = fromRow + 2 * direction;
        if (fromRow === startRow && isValidPosition(doubleForwardRow, fromCol) && board[doubleForwardRow] && isEmptySquare(board[doubleForwardRow][fromCol])) {
          console.log('Adding double forward move:', { row: doubleForwardRow, col: fromCol });
          moves.push({ row: doubleForwardRow, col: fromCol });
        }
      } else {
        console.log('Forward move blocked or invalid');
      }

      // Diagonal captures
      [-1, 1].forEach(offset => {
        const targetRow = fromRow + direction;
        const targetCol = fromCol + offset;
        if (isValidPosition(targetRow, targetCol) && board[targetRow]) {
          const targetPiece = board[targetRow][targetCol];
          if (targetPiece && targetPiece !== '' && getPieceColor(targetPiece) !== color) {
            moves.push({ row: targetRow, col: targetCol });
          }
        }
      });
      break;

    case 'N': // Knight
      const knightMoves = [
        [-2, -1], [-2, 1], [-1, -2], [-1, 2],
        [1, -2], [1, 2], [2, -1], [2, 1]
      ];
      knightMoves.forEach(([dRow, dCol]) => {
        addMove(fromRow + dRow, fromCol + dCol);
      });
      break;

    case 'B': // Bishop
      const bishopDirections = [[-1, -1], [-1, 1], [1, -1], [1, 1]];
      bishopDirections.forEach(([dRow, dCol]) => {
        for (let i = 1; i < 8; i++) {
          if (!addMove(fromRow + i * dRow, fromCol + i * dCol)) break;
        }
      });
      break;

    case 'R': // Rook
      const rookDirections = [[-1, 0], [1, 0], [0, -1], [0, 1]];
      rookDirections.forEach(([dRow, dCol]) => {
        for (let i = 1; i < 8; i++) {
          if (!addMove(fromRow + i * dRow, fromCol + i * dCol)) break;
        }
      });
      break;

    case 'Q': // Queen (combination of rook and bishop)
      const queenDirections = [
        [-1, -1], [-1, 0], [-1, 1],
        [0, -1], [0, 1],
        [1, -1], [1, 0], [1, 1]
      ];
      queenDirections.forEach(([dRow, dCol]) => {
        for (let i = 1; i < 8; i++) {
          if (!addMove(fromRow + i * dRow, fromCol + i * dCol)) break;
        }
      });
      break;

    case 'K': // King
      const kingMoves = [
        [-1, -1], [-1, 0], [-1, 1],
        [0, -1], [0, 1],
        [1, -1], [1, 0], [1, 1]
      ];
      kingMoves.forEach(([dRow, dCol]) => {
        addMove(fromRow + dRow, fromCol + dCol);
      });
      break;
  }

  return moves;
};

// Check if a specific square is under attack by the opponent
const isSquareUnderAttack = (board, row, col, byColor) => {
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      const piece = board[r][c];
      if (piece && getPieceColor(piece) === byColor) {
        const moves = getPossibleMoves(board, r, c);
        if (moves.some(move => move.row === row && move.col === col)) {
          return true;
        }
      }
    }
  }
  return false;
};

// Find king position for a color
const findKing = (board, color) => {
  const kingPiece = color === 'white' ? PIECES.WHITE_KING : PIECES.BLACK_KING;
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      if (board[r][c] === kingPiece) {
        return { row: r, col: c };
      }
    }
  }
  return null;
};

// Check if a color is in check
const isInCheck = (board, color) => {
  const kingPos = findKing(board, color);
  if (!kingPos) return false;

  const opponentColor = color === 'white' ? 'black' : 'white';
  return isSquareUnderAttack(board, kingPos.row, kingPos.col, opponentColor);
};

// Simulate a move and check if it leaves the king in check
const wouldBeInCheck = (board, fromRow, fromCol, toRow, toCol, color) => {
  // Create a copy of the board
  const newBoard = board.map(row => [...row]);

  // Make the move
  newBoard[toRow][toCol] = newBoard[fromRow][fromCol];
  newBoard[fromRow][fromCol] = null;

  return isInCheck(newBoard, color);
};

// Get legal moves including en passant and castling
export const getLegalMoves = (gameState, fromRow, fromCol) => {
  const { board, currentTurn, enPassantTarget, castlingRights } = gameState;
  const piece = board[fromRow][fromCol];

  if (!piece || getPieceColor(piece) !== currentTurn) {
    return [];
  }

  const possibleMoves = getPossibleMoves(board, fromRow, fromCol);
  console.log('Possible moves before check filter:', possibleMoves);
  const legalMoves = [];

  // Filter moves that would leave king in check
  for (const move of possibleMoves) {
    const wouldBeCheck = wouldBeInCheck(board, fromRow, fromCol, move.row, move.col, currentTurn);
    console.log('Move', move, 'wouldBeInCheck:', wouldBeCheck);
    if (!wouldBeCheck) {
      legalMoves.push(move);
    }
  }
  console.log('Legal moves after check filter:', legalMoves);

  const type = getPieceType(piece);

  // Add en passant for pawns
  if (type === 'P' && enPassantTarget) {
    const direction = currentTurn === 'white' ? 1 : -1;
    if (fromRow === (currentTurn === 'white' ? 4 : 3)) {
      if (Math.abs(fromCol - enPassantTarget.col) === 1 &&
          enPassantTarget.row === fromRow + direction) {
        if (!wouldBeInCheck(board, fromRow, fromCol, enPassantTarget.row, enPassantTarget.col, currentTurn)) {
          legalMoves.push({ ...enPassantTarget, isEnPassant: true });
        }
      }
    }
  }

  // Add castling for king
  if (type === 'K' && !isInCheck(board, currentTurn)) {
    const row = currentTurn === 'white' ? 0 : 7;

    // Kingside castling
    if (castlingRights[currentTurn].kingside) {
      if (!board[row][5] && !board[row][6]) {
        if (!isSquareUnderAttack(board, row, 5, currentTurn === 'white' ? 'black' : 'white') &&
            !isSquareUnderAttack(board, row, 6, currentTurn === 'white' ? 'black' : 'white')) {
          legalMoves.push({ row, col: 6, isCastling: true, rookCol: 7 });
        }
      }
    }

    // Queenside castling
    if (castlingRights[currentTurn].queenside) {
      if (!board[row][1] && !board[row][2] && !board[row][3]) {
        if (!isSquareUnderAttack(board, row, 2, currentTurn === 'white' ? 'black' : 'white') &&
            !isSquareUnderAttack(board, row, 3, currentTurn === 'white' ? 'black' : 'white')) {
          legalMoves.push({ row, col: 2, isCastling: true, rookCol: 0 });
        }
      }
    }
  }

  return legalMoves;
};

// Check if a move is legal
export const isValidMove = (gameState, fromRow, fromCol, toRow, toCol) => {
  const legalMoves = getLegalMoves(gameState, fromRow, fromCol);
  return legalMoves.some(move => move.row === toRow && move.col === toCol);
};

// Make a move and return new game state
export const makeMove = (gameState, fromRow, fromCol, toRow, toCol, promotionPiece = 'Q') => {
  const { board, currentTurn, capturedPieces, castlingRights, halfmoveClock, fullmoveNumber } = gameState;

  const newBoard = board.map(row => [...row]);
  const piece = newBoard[fromRow][fromCol];
  const capturedPiece = newBoard[toRow][toCol];
  const pieceType = getPieceType(piece);

  // Track captured pieces (with fallback for older games)
  const newCapturedPieces = {
    white: [...(capturedPieces?.white || [])],
    black: [...(capturedPieces?.black || [])],
  };

  // Only track captured pieces if it's an actual piece (not empty string)
  if (capturedPiece && capturedPiece !== '') {
    const capturedColor = getPieceColor(capturedPiece);
    newCapturedPieces[capturedColor].push(capturedPiece);
  }

  // Check for special moves
  const legalMoves = getLegalMoves(gameState, fromRow, fromCol);
  const moveData = legalMoves.find(m => m.row === toRow && m.col === toCol);

  let newEnPassantTarget = null;

  // Handle en passant capture
  if (moveData?.isEnPassant) {
    const capturedPawnRow = currentTurn === 'white' ? toRow - 1 : toRow + 1;
    const capturedPawn = newBoard[capturedPawnRow][toCol];
    if (capturedPawn && capturedPawn !== '') {
      const capturedColor = getPieceColor(capturedPawn);
      newCapturedPieces[capturedColor].push(capturedPawn);
      newBoard[capturedPawnRow][toCol] = '';
    }
  }

  // Handle castling
  if (moveData?.isCastling) {
    const rookFromCol = moveData.rookCol;
    const rookToCol = toCol === 6 ? 5 : 3;
    newBoard[toRow][rookToCol] = newBoard[toRow][rookFromCol];
    newBoard[toRow][rookFromCol] = '';
  }

  // Make the move (use empty string for empty squares)
  newBoard[toRow][toCol] = piece;
  newBoard[fromRow][fromCol] = '';

  // Handle pawn promotion
  if (pieceType === 'P' && (toRow === 0 || toRow === 7)) {
    const color = currentTurn === 'white' ? 'w' : 'b';
    newBoard[toRow][toCol] = color + promotionPiece;
  }

  // Set en passant target if pawn moved two squares
  if (pieceType === 'P' && Math.abs(toRow - fromRow) === 2) {
    newEnPassantTarget = {
      row: (fromRow + toRow) / 2,
      col: toCol,
    };
  }

  // Update castling rights
  const newCastlingRights = {
    white: { ...castlingRights.white },
    black: { ...castlingRights.black },
  };

  if (pieceType === 'K') {
    newCastlingRights[currentTurn].kingside = false;
    newCastlingRights[currentTurn].queenside = false;
  }

  if (pieceType === 'R') {
    if (fromRow === 0 && fromCol === 0) newCastlingRights.white.queenside = false;
    if (fromRow === 0 && fromCol === 7) newCastlingRights.white.kingside = false;
    if (fromRow === 7 && fromCol === 0) newCastlingRights.black.queenside = false;
    if (fromRow === 7 && fromCol === 7) newCastlingRights.black.kingside = false;
  }

  // If rook was captured, update castling rights
  if (capturedPiece && getPieceType(capturedPiece) === 'R') {
    if (toRow === 0 && toCol === 0) newCastlingRights.white.queenside = false;
    if (toRow === 0 && toCol === 7) newCastlingRights.white.kingside = false;
    if (toRow === 7 && toCol === 0) newCastlingRights.black.queenside = false;
    if (toRow === 7 && toCol === 7) newCastlingRights.black.kingside = false;
  }

  // Update halfmove clock (for fifty-move rule)
  const newHalfmoveClock = (pieceType === 'P' || capturedPiece) ? 0 : halfmoveClock + 1;

  // Update fullmove number
  const newFullmoveNumber = currentTurn === 'black' ? fullmoveNumber + 1 : fullmoveNumber;

  // Switch turns
  const nextTurn = currentTurn === 'white' ? 'black' : 'white';

  // Check for check/checkmate/stalemate
  const inCheck = isInCheck(newBoard, nextTurn);
  const hasLegalMoves = hasAnyLegalMove(newBoard, nextTurn, newCastlingRights, newEnPassantTarget);

  let isGameOver = false;
  let winner = null;
  let isDraw = false;
  let drawReason = null;

  if (!hasLegalMoves) {
    isGameOver = true;
    if (inCheck) {
      // Checkmate
      winner = currentTurn;
    } else {
      // Stalemate
      isDraw = true;
      drawReason = 'stalemate';
    }
  } else if (newHalfmoveClock >= 100) {
    // Fifty-move rule (100 half-moves)
    isGameOver = true;
    isDraw = true;
    drawReason = 'fifty-move-rule';
  } else if (isInsufficientMaterial(newBoard)) {
    isGameOver = true;
    isDraw = true;
    drawReason = 'insufficient-material';
  }

  return {
    ...gameState,
    board: newBoard,
    currentTurn: nextTurn,
    capturedPieces: newCapturedPieces,
    castlingRights: newCastlingRights,
    enPassantTarget: newEnPassantTarget,
    halfmoveClock: newHalfmoveClock,
    fullmoveNumber: newFullmoveNumber,
    inCheck,
    isGameOver,
    winner,
    isDraw,
    drawReason,
    moveHistory: [
      ...(gameState.moveHistory || []),
      { from: { row: fromRow, col: fromCol }, to: { row: toRow, col: toCol }, piece, capturedPiece }
    ],
  };
};

// Check if any legal move exists for a color
const hasAnyLegalMove = (board, color, castlingRights, enPassantTarget) => {
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      const piece = board[r][c];
      if (piece && getPieceColor(piece) === color) {
        const gameState = { board, currentTurn: color, castlingRights, enPassantTarget };
        const moves = getLegalMoves(gameState, r, c);
        if (moves.length > 0) return true;
      }
    }
  }
  return false;
};

// Check for insufficient material
const isInsufficientMaterial = (board) => {
  const pieces = [];

  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      if (board[r][c]) {
        pieces.push(board[r][c]);
      }
    }
  }

  // King vs King
  if (pieces.length === 2) return true;

  // King and Bishop vs King or King and Knight vs King
  if (pieces.length === 3) {
    const types = pieces.map(getPieceType);
    if (types.includes('B') || types.includes('N')) return true;
  }

  // King and Bishop vs King and Bishop (same color bishops)
  if (pieces.length === 4) {
    const bishops = pieces.filter(p => getPieceType(p) === 'B');
    if (bishops.length === 2) {
      // Check if bishops are on same color squares
      let bishopSquareColors = [];
      for (let r = 0; r < 8; r++) {
        for (let c = 0; c < 8; c++) {
          if (board[r][c] && getPieceType(board[r][c]) === 'B') {
            bishopSquareColors.push((r + c) % 2);
          }
        }
      }
      if (bishopSquareColors[0] === bishopSquareColors[1]) return true;
    }
  }

  return false;
};

// Convert position to algebraic notation (e.g., [0, 0] -> "a1")
export const toAlgebraic = (row, col) => {
  return String.fromCharCode(97 + col) + (row + 1);
};

// Convert algebraic notation to position
export const fromAlgebraic = (notation) => {
  return {
    row: parseInt(notation[1]) - 1,
    col: notation.charCodeAt(0) - 97,
  };
};

export { PIECES, getPieceColor, getPieceType };
